import { Image, message } from 'antd';
import moment from 'moment';
import React, { type FC, useEffect, useRef, useState } from 'react';
import {
  MdOutlineAddPhotoAlternate,
  MdOutlineClose,
  MdSend,
} from 'react-icons/md';

import useService from '@/lib/hooks/useService';
import type { RealtimeMessages } from '@/types/database/realtimeMessages';

import { uploadImage } from './helpers';
import styles from './index.module.scss';

const Messages: FC<{
  feedbackId: string;
  user: { id: string; name: string };
}> = ({ feedbackId, user }) => {
  const [messages, setMessages] = useState<RealtimeMessages>([]);
  const [newMessage, setNewMessage] = useState('');
  const [currentUploadImage, setCurrentUploadImage] = useState<File | null>(
    null
  );

  const [realtimeMessages, mutate, realtimeMessagesService] = useService({
    collectionName: 'realtimeMessages',
    query: {
      associatedEntity: 'feedback',
      entityId: feedbackId,
      $sort: {
        createdTime: -1,
      },
    },
    swrConfig: { refreshInterval: 1000 * 3 },
  });

  const imgInputRef = useRef<HTMLInputElement>(null);

  const imageUrl =
    (currentUploadImage && URL.createObjectURL(currentUploadImage)) || '';

  useEffect(() => {
    if (realtimeMessages.length > 0) {
      setMessages(realtimeMessages);
      realtimeMessagesService.patch(
        null,
        {
          isRead: true,
        },
        {
          query: {
            isRead: false,
            associatedEntity: 'feedback',
            entityId: feedbackId,
            'sender.id': { $ne: user.id },
          },
        }
      );
    }
  }, [feedbackId, realtimeMessages, realtimeMessagesService, user.id]);

  // 發送消息
  async function sendMessage(
    content: RealtimeMessages[number]['content'],
    feedbackId: string,
    user: { id: string; name: string }
  ): Promise<void> {
    try {
      await realtimeMessagesService.create({
        associatedEntity: 'feedback',
        entityId: feedbackId,
        sender: user,
        receiver: null,
        content,
        createdTime: Date.now(),
        isRead: false,
      });
      mutate();
    } catch (error) {
      console.error(error);
      message.error('消息發送失敗');
    }
  }

  // 更新後的 handleSendMessage
  const handleSendMessage = async (): Promise<void> => {
    if (!newMessage && !currentUploadImage) {
      return;
    }

    let content: RealtimeMessages[number]['content'] | null = null;

    if (currentUploadImage) {
      const imagePath = await uploadImage(currentUploadImage);
      if (imagePath) {
        content = { type: 'image', image: imagePath };
        setCurrentUploadImage(null);
      }
    } else if (newMessage) {
      content = { type: 'text', text: newMessage };
      setNewMessage('');
    }

    if (content) {
      await sendMessage(content, feedbackId, user);
    }
  };

  return (
    <div className={styles.messageArea}>
      <div className={styles.messages}>
        {messages.map((message) => {
          const isUserMessage = message.sender.id === user.id;
          return (
            <div
              key={message._id}
              className={styles.message}
              style={{
                alignItems: isUserMessage ? 'flex-end' : 'flex-start',
              }}
            >
              <p className={styles.sender}>{message.sender.name}</p>
              {message.content.type === 'text' && (
                <div className={styles.content}>
                  <p>{message.content.text}</p>
                </div>
              )}
              {message.content.type === 'image' && (
                <div className={styles.content}>
                  <Image src={message.content.image} />
                </div>
              )}
              <p className={styles.createdAt}>
                {moment(message.createdTime).format('M/D HH:mm')}
              </p>
            </div>
          );
        })}
      </div>
      <div className={styles.inputArea}>
        <div className={styles.inputs}>
          <label htmlFor="imgUpload" className={styles.uploadButton}>
            <input
              type="file"
              id="imgUpload"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) {
                  setCurrentUploadImage(null);
                  e.target.value = '';
                  return;
                }
                if (file.size > 1024 * 1024 * 5) {
                  message.error('檔案大小超過 5MB');
                  return;
                }
                setCurrentUploadImage(file);
              }}
              ref={imgInputRef}
              accept="image/*"
            />
            <MdOutlineAddPhotoAlternate />
          </label>
          <input
            className={styles.input}
            type="text"
            placeholder="請輸入訊息"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyUpCapture={(e) => {
              if (e.key === 'Enter') {
                handleSendMessage();
              }
            }}
            disabled={!!imageUrl}
          />
          <button className={styles.sendButton} onClick={handleSendMessage}>
            <MdSend />
          </button>
        </div>
        {imageUrl && (
          <div className={styles.uploadPreview}>
            <img src={imageUrl} alt="upload image" />
            <button
              className={styles.removeButton}
              onClick={() => {
                setCurrentUploadImage(null);
                if (imgInputRef.current) {
                  imgInputRef.current.value = '';
                }
              }}
            >
              <MdOutlineClose />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
